import type { ModelInfo } from '@gofranz/gpt-muse-common';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconBrain, IconBulb, IconCash, IconSearch, IconX } from '@tabler/icons-react';
import React, { useState } from 'react';
import { getModelBadges } from '../../lib/model-ui-utils';
import { formatPricing } from '../../lib/model-utils';

interface ModelSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  models: ModelInfo[];
  selectedModel: string;
  onModelSelect: (modelId: string) => void;
  isLoggedIn?: boolean;
}

export const ModelSelectionModal = React.memo<ModelSelectionModalProps>(
  ({ opened, onClose, models, selectedModel, onModelSelect, isLoggedIn = false }) => {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredModels = models.filter(
      (model) =>
        model.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        model.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        model.id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSelectModel = (modelId: string) => {
      const model = models.find((m) => m.id === modelId);
      // Prevent selection of paid models when not logged in
      if (!isLoggedIn && model && !model.free_tier) {
        return;
      }
      onModelSelect(modelId);
      onClose();
    };

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Group gap="xs">
            <IconBrain size={20} />
            <Text size="lg" fw={600}>
              Select Model
            </Text>
          </Group>
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          {!isLoggedIn && (
            <Box
              p="sm"
              style={{
                backgroundColor: 'var(--mantine-color-blue-light)',
                borderRadius: '6px',
                border: '1px solid var(--mantine-color-blue-outline)',
              }}
            >
              <Text size="sm" c="blue">
                Choose from free models (best-effort service) or try premium models like{' '}
                <strong>Gemini 2.5 Flash Lite</strong> available on a trial basis.
              </Text>
            </Box>
          )}

          <TextInput
            placeholder="Search models..."
            leftSection={<IconSearch size={16} />}
            rightSection={
              searchQuery ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setSearchQuery('')}
                  size="sm"
                >
                  <IconX size={14} />
                </ActionIcon>
              ) : null
            }
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
          />

          <ScrollArea h={400} offsetScrollbars>
            <Stack gap="xs">
              {filteredModels.map((model) => {
                const isDisabled = !isLoggedIn && !model.free_tier;

                return (
                  <Card
                    key={model.id}
                    p="md"
                    withBorder
                    style={{
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.5 : 1,
                      border:
                        selectedModel === model.id
                          ? '2px solid var(--mantine-primary-color-filled)'
                          : undefined,
                      backgroundColor:
                        selectedModel === model.id
                          ? 'var(--mantine-primary-color-light)'
                          : undefined,
                    }}
                    onClick={() => handleSelectModel(model.id)}
                  >
                    <Stack gap="sm">
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="md" fw={600} truncate>
                            {model.name}
                          </Text>
                          <Text size="xs" c="dimmed" truncate>
                            {model.id}
                          </Text>
                        </Box>

                        <Group gap={4} wrap="nowrap">
                          {getModelBadges(model)}
                          {isDisabled && (
                            <Badge size="sm" color="red" variant="light">
                              Login Required
                            </Badge>
                          )}
                        </Group>
                      </Group>

                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {model.description}
                      </Text>

                      <Divider />

                      <Group gap="lg" wrap="nowrap">
                        <Group gap={4} align="center" style={{ flex: 1, minWidth: 0 }}>
                          <IconCash size={14} color="gray" />
                          <Text size="xs" c="dimmed" truncate>
                            {formatPricing(model)}
                          </Text>
                        </Group>

                        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                          {model.provider_count} provider{model.provider_count !== 1 ? 's' : ''}
                        </Text>
                      </Group>
                    </Stack>
                  </Card>
                );
              })}

              {filteredModels.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  No models found matching "{searchQuery}"
                </Text>
              )}
            </Stack>
          </ScrollArea>

          <Box
            p="xs"
            style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '4px' }}
          >
            <Text size="xs" c="dimmed" mb={4}>
              <strong>Reasoning Support:</strong>
            </Text>
            <Group gap="md">
              <Group gap={4}>
                <IconBulb size={12} color="var(--mantine-color-yellow-6)" />
                <Text size="xs" c="dimmed">
                  Reasoning - Always enabled
                </Text>
              </Group>
              <Group gap={4}>
                <IconBulb size={12} color="var(--mantine-color-orange-6)" />
                <Text size="xs" c="dimmed">
                  Reasoning* - Can be enabled/disabled
                </Text>
              </Group>
            </Group>
          </Box>

          <Group justify="flex-end">
            <Button variant="light" onClick={onClose}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }
);

ModelSelectionModal.displayName = 'ModelSelectionModal';
